import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { rupees } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHead, Kpi, Loading, BrandDot } from "@/components/common";
import { Users, Coins, Banknote, QrCode, Gamepad2, Star } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";

export default function Overview() {
  const { selectedBrand, brands } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [setup, setSetup] = useState(null);
  const goReport = (tab) => nav(`/app/reports?tab=${tab}`);

  useEffect(() => {
    const b = selectedBrand === "ALL" ? "" : `?brand=${selectedBrand}`;
    api.get(`/overview${b}`).then((r) => setData(r.data));
    api.get("/setup-summary").then((r) => setSetup(r.data));
  }, [selectedBrand]);

  if (!data) return <Loading />;
  const brandName = (id) => brands.find((b) => b.id === id)?.name || id;

  return (
    <div>
      <PageHead overline="Operations Command Center" title="Overview"
        desc="Live KPIs computed from stored sandbox records across all authorized brands." />

      {setup && (
        <div className="mb-6 bg-violet-50 border border-violet-100 rounded-xl p-4 flex flex-wrap gap-6 text-sm">
          <span className="font-semibold text-violet-800">Setup status:</span>
          <span>{setup.brands} brands · {setup.skus} SKUs</span>
          <span className="text-amber-700 font-medium">{setup.reward_values_pending} reward values pending</span>
          <span className="text-amber-700 font-medium">{setup.source_codes_missing} source codes missing</span>
          <span className="text-amber-700 font-medium">{setup.categories_needing_review} categories need review</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <Kpi testid="kpi-members" label="Members" value={data.members.toLocaleString("en-IN")} icon={Users} accent="#6D28D9" onClick={() => goReport("registration")} />
        <Kpi testid="kpi-points" label="Points Issued" value={data.points_issued.toLocaleString("en-IN")} icon={Coins} accent="#D97706" onClick={() => goReport("points_issued")} />
        <Kpi testid="kpi-payouts" label="Payouts Disbursed" value={rupees(data.payouts_paise)} icon={Banknote} accent="#15803D" onClick={() => goReport("fund_transfer")} />
        <Kpi testid="kpi-scans" label="Valid Scans" value={data.scans.toLocaleString("en-IN")} icon={QrCode} accent="#0EA5E9" onClick={() => goReport("behaviour")} />
        <Kpi testid="kpi-games" label="Game Plays" value={data.game_plays.toLocaleString("en-IN")} icon={Gamepad2} accent="#DB2777" onClick={() => goReport("gaming")} />
        <Kpi testid="kpi-nps" label="Avg Feedback" value={`${data.nps || "—"} / 5`} icon={Star} accent="#F59E0B" onClick={() => goReport("feedback")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-4 font-head">Daily Scan Activity</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6D28D9" stopOpacity={0.3} /><stop offset="100%" stopColor="#6D28D9" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="scans" stroke="#6D28D9" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
          {(!data.trend || data.trend.length === 0) && <p className="text-xs text-slate-400 text-center -mt-10">No live scan activity yet — claim a code in the simulator.</p>}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-4 font-head">Points by Brand</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.by_brand}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="brand_id" tick={{ fontSize: 11 }} tickFormatter={brandName} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="points" fill="#6D28D9" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
